import React, { useContext, useState } from "react";
import { IntlContext, FlashContext } from "context";
import Form, { Mode } from "../Form";
import { useAd, useDeleteAd } from "hooks/ad";
import { useParams, useNavigate } from "react-router";
import { Loader } from "ui/Loader";
import moment from "moment";
import { Link } from "react-router-dom";
import Button from "ui/Button";
import ConfirmModal from "ui/Modal/ConfirmModal";
import Seo from "components/Seo";
import { ROUTES, buildRoute } from "routes";

const AdView: React.FC = () => {
    const intl = useContext(IntlContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const flashMessage = useContext(FlashContext);

    // Retrieve ad data through TanStack Query.
    const { data, isLoading } = useAd(id);
    const deleteAdMutation = useDeleteAd();

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const handleShowConfirmModalDelete = () => {
        setShowDeleteModal(true);
    };

    const handleOnClickDeleteAd = async () => {
        if (!data) return;
        await deleteAdMutation.mutateAsync(data.id);

        navigate(ROUTES.MY_ADS);
        flashMessage?.addSuccess(intl.formatMessage("ad.message.delete_success"));
    };

    if (isLoading || !data) {
        return <Loader />;
    }

    // Construction des balises meta une fois les données chargées.
    // NB : la page est aujourd'hui authentifiée → noindex. À passer à false quand
    // la route deviendra publique (ticket SEO dans doc/seo-rendertron.md).
    const categoryLabel = intl.formatMessage(`service.label.category.${data.category}`);
    const subcategoryLabel = data.subcategory
        ? intl.formatMessage(`service.label.subcategory.${data.subcategory}`)
        : "";
    const seoTitle = `${subcategoryLabel || categoryLabel} — ${data.client?.city ?? ""} | Twoghether`.trim();
    const seoDescription = data.description?.slice(0, 160);

    const cityLabel = data.client?.city
        ? `${data.client.city}${data.client.postcode ? ` (${data.client.postcode})` : ""}`
        : "";

    return (
        <div className="min-h-screen bg-gray-50">
            <Seo title={seoTitle} description={seoDescription} ogType="article" noindex={true} />
            <ConfirmModal
                show={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleOnClickDeleteAd}
                title={intl.formatMessage("ad.label.modal.title")}
                message={intl.formatMessage("ad.label.modal.message")}
                confirmButton={intl.formatMessage("generic.action.confirm")}
                cancelButton={intl.formatMessage("generic.action.cancel")}
            />
            <div className="max-w-5xl mx-auto px-4 py-6">
                {/* Bouton retour */}
                <Link
                    to={ROUTES.MY_ADS}
                    className="inline-flex items-center gap-2 bg-white rounded-xl px-4 py-2 mb-6 shadow-sm hover:shadow-md transition text-sm font-medium text-gray-700"
                >
                    <span className="text-lg">←</span>
                    {intl.formatMessage("generic.action.back")}
                </Link>

                {/* Carte principale : titre, badges, description */}
                <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
                    {/* Top row : badge fréquence + actions */}
                    <div className="flex justify-between items-start mb-4">
                        <span className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
                            <span className="text-red-500">⏱</span>
                            <span className="text-red-500">Une seule fois</span>
                        </span>
                        <div className="flex gap-2">
                            <button className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-red-500" title="Signaler">
                                ⚠
                            </button>
                            <button className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-red-500" title="Ajouter aux favoris">
                                ♡
                            </button>
                            <button className="w-10 h-10 rounded-xl bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-red-500" title="Partager">
                                ⌘
                            </button>
                        </div>
                    </div>

                    {/* Titre */}
                    <h1 className="text-3xl font-extrabold text-gray-900 mb-4">
                        {data.title}
                    </h1>

                    {/* Badges (catégorie / sous-catégorie / ville / date) */}
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-xs font-semibold uppercase px-3 py-1.5 rounded-full">
                            <span>✦</span>
                            {categoryLabel}
                        </span>
                        {data.subcategory && (
                            <span className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-xs font-semibold uppercase px-3 py-1.5 rounded-full">
                                <span>✦</span>
                                {subcategoryLabel}
                            </span>
                        )}
                        {cityLabel && (
                            <span className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">
                                <span className="text-emerald-600">📍</span>
                                {cityLabel}
                            </span>
                        )}
                        <span className="inline-flex items-center gap-2 text-gray-500 text-xs font-semibold px-3 py-1.5">
                            <span>📅</span>
                            {moment(data.created_at).fromNow()}
                        </span>
                    </div>

                    {/* Edit / Delete (fonctionnalités existantes conservées) */}
                    <div className="flex gap-2 mb-6">
                        <Link
                            className="inline-flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
                            to={buildRoute.myAdEdit(data.id)}
                        >
                            <span className="icon icon-pen icon--left"></span>
                            {intl.formatMessage("ad.label.edit")}
                        </Link>
                        <Button
                            className="inline-flex items-center gap-2 bg-red-50 border-red-500 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg text-sm font-medium"
                            onClick={() => handleShowConfirmModalDelete()}
                        >
                            <span className="icon icon-trash icon--left"></span>
                            {intl.formatMessage("ad.label.delete")}
                        </Button>
                    </div>

                    {/* Description */}
                    <div className="border-l-4 border-indigo-500 pl-3 mb-3">
                        <h2 className="text-xs font-bold tracking-wider uppercase text-gray-800">Description</h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                        {data.description}
                    </p>

                    {/* Infos rapides : durée */}
                    {data.duration && (
                        <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100 text-sm text-gray-600">
                            <div>
                                <span className="font-semibold text-gray-800">Durée :</span>{" "}
                                {data.duration} {intl.formatMessage("ad.label.duration")}
                            </div>
                        </div>
                    )}
                </div>

                {/* Section Localisation (NOUVEAU - non implémenté) */}
                <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
                    <div className="border-l-4 border-indigo-500 pl-3 mb-4">
                        <h2 className="text-xs font-bold tracking-wider uppercase text-red-500">Localisation</h2>
                    </div>

                    <div className="relative w-full h-72 rounded-xl overflow-hidden bg-gradient-to-br from-blue-100 via-emerald-50 to-blue-50 border border-gray-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-10 h-10 rounded-full bg-indigo-600 border-4 border-white shadow-lg flex items-center justify-center">
                                <span className="text-white text-lg">📍</span>
                            </div>
                        </div>
                        <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur rounded-lg shadow p-3 flex items-center gap-3">
                            <span className="text-red-500">📍</span>
                            <div>
                                <div className="text-sm font-bold text-red-500">{data.client?.city ?? "Localisation"}</div>
                                <div className="text-xs text-red-500">Zone approximative pour la confidentialité</div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-4 bg-indigo-50 rounded-xl p-3 flex items-start gap-2 text-sm">
                        <span className="text-red-500">🛡</span>
                        <p className="text-red-500">
                            <span className="font-bold">Confidentialité :</span> L'adresse exacte n'est partagée qu'après acceptation de votre candidature par l'annonceur.
                        </p>
                    </div>
                </div>

                {/* Section À propos de l'annonceur (NOUVEAU - non implémenté) */}
                <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
                    <div className="border-l-4 border-indigo-500 pl-3 mb-4">
                        <h2 className="text-xs font-bold tracking-wider uppercase text-red-500">À propos de l'annonceur</h2>
                    </div>

                    <div className="flex items-start gap-4 mb-4">
                        <div className="relative">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-200 to-purple-200 flex items-center justify-center text-2xl">
                                👤
                            </div>
                            <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 border-2 border-white flex items-center justify-center text-white text-xs">✓</span>
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                                <span className="text-lg font-bold text-red-500">Julie S.</span>
                                <span className="bg-indigo-100 text-red-500 text-xs font-bold uppercase px-2 py-0.5 rounded">Vérifié</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <span className="text-red-500">⭐ 4.8 (23 avis)</span>
                                <span className="text-gray-300">•</span>
                                <span className="text-red-500">Membre depuis 2023</span>
                            </div>
                            <p className="text-sm text-red-500 mt-2">
                                Mère de famille de 45 ans, je vis dans le quartier depuis 10 ans. Je fais régulièrement appel à des services de proximité et j'apprécie la qualité du travail bien fait.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="text-2xl font-extrabold text-red-500">95%</div>
                            <div className="text-xs uppercase tracking-wider text-red-500 mt-1">Taux réponse</div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="text-2xl font-extrabold text-red-500">1h</div>
                            <div className="text-xs uppercase tracking-wider text-red-500 mt-1">Temps réponse</div>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <div className="text-2xl font-extrabold text-red-500">12</div>
                            <div className="text-xs uppercase tracking-wider text-red-500 mt-1">Missions</div>
                        </div>
                    </div>

                    <button className="w-full mt-4 bg-red-50 hover:bg-red-100 text-red-500 font-semibold py-3 rounded-xl">
                        Postuler à cette annonce
                    </button>
                </div>

                {/* Détails complets (formulaire en lecture seule conservé pour préserver toutes les fonctionnalités existantes) */}
                <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 mb-6">
                    <div className="border-l-4 border-indigo-500 pl-3 mb-4">
                        <h2 className="text-xs font-bold tracking-wider uppercase text-gray-800">Détails complets</h2>
                    </div>
                    <Form initialValues={data} mode={Mode.VIEW} />
                </div>
            </div>
        </div>
    );
};

export default AdView;
